/**
 * Maps raw publish-task errors (TikTok API responses + our own backend codes)
 * to user-readable Spanish text. Falls back to the raw string (truncated) when
 * no pattern matches — so callers always get *something* to render.
 *
 * Used by:
 *   - mis-publicaciones-page (failed task cards)
 *   - share-modal (live publish error toast)
 */

export interface HumanizedPublishError {
  /** Short header for the human-friendly explanation. Optional. */
  title?: string;
  /** Full friendly message — always present. */
  message: string;
  /** Original raw error, kept around so a "Ver detalle" expander can show it. */
  raw?: string;
  /** True when we recognized the error and surfaced a clean message; false
   *  when we fell back to the raw string. Useful for callers that want to
   *  style differently in each case. */
  recognized: boolean;
}

type Pattern = [
  matcher: string | RegExp,
  humanized: Omit<HumanizedPublishError, 'raw' | 'recognized'>,
];

const PATTERNS: Pattern[] = [
  [
    'unaudited_client_can_only_post_to_private_accounts',
    {
      title:   'TikTok todavía no auditó nuestra integración',
      message: 'Hasta que TikTok complete el audit del Content Posting API, solo aceptan publicaciones privadas. Reintentá: la próxima va a salir como privada (solo vos la ves) automáticamente.',
    },
  ],
  [
    'reached_active_user_cap',
    {
      title:   'Llegamos al cupo de cuentas TikTok',
      message: 'TikTok limita a 5 usuarios activos hasta que la app pase el "scale up audit". Si necesitás agregar una cuenta más, contactanos para liberar un slot o pedir el scale up a TikTok.',
    },
  ],
  [
    'url_ownership_unverified',
    {
      title:   'TikTok rechazó la publicación (verificación pendiente)',
      // TikTok sobrecarga este código: dominio sin verificar en el developer
      // portal (config nuestra) O token sin el scope video.upload (se arregla
      // reconectando la cuenta). Sugerimos primero lo que el usuario puede
      // resolver solo.
      message: 'Probá desconectar y reconectar tu cuenta de TikTok (puede faltarle un permiso de publicación). Si sigue fallando, avisanos: puede ser una configuración pendiente de nuestro lado en TikTok.',
    },
  ],
  [
    'spam_risk_too_many_pending_share',
    {
      title:   'Demasiadas publicaciones pendientes en TikTok',
      message: 'TikTok detectó muchos posts pendientes en tu cuenta y bloqueó este intento. Esperá unos minutos y reintentá.',
    },
  ],
  [
    // TikTok's per-creator daily posting cap (~15 posts/24h). Maps to the
    // guideline's "creator cannot make more posts at this moment" — we surface
    // it at post time and tell the user to try again later (creator_info has no
    // reliable pre-check field for this, so we handle the error path).
    'spam_risk_too_many_posts',
    {
      title:   'Llegaste al límite diario de TikTok',
      message: 'TikTok permite una cantidad limitada de publicaciones por día en tu cuenta. Probá de nuevo más tarde.',
    },
  ],
  [
    // Más específico que 'spam_risk' (que es substring) → tiene que ir ANTES en la lista.
    'spam_risk_user_banned_from_posting',
    {
      title:   'Tu cuenta de TikTok no puede publicar ahora',
      message: 'TikTok bloqueó temporalmente las publicaciones desde tu cuenta. Suele levantarse solo; esperá un tiempo y reintentá. Si persiste, revisá el estado de tu cuenta en la app de TikTok.',
    },
  ],
  [
    'spam_risk',
    {
      title:   'TikTok marcó el post como sospechoso',
      message: 'TikTok bloqueó esta publicación por filtros antispam. Esperá unas horas y reintentá; si persiste, contactanos.',
    },
  ],
  [
    /rate.?limit/i,
    {
      title:   'Límite de publicaciones alcanzado',
      message: 'TikTok te limitó por publicar muchos posts en poco tiempo. Esperá un rato y reintentá.',
    },
  ],
  [
    /access[_ ]token.*(expired|invalid|revoked)/i,
    {
      title:   'Sesión de TikTok venció',
      message: 'El token de tu cuenta TikTok ya no es válido. Andá a /conexiones y reconectá la cuenta.',
    },
  ],
  [
    /scope_not_authorized|missing.*scope|insufficient.*scope/i,
    {
      title:   'Permisos insuficientes en TikTok',
      message: 'Tu cuenta TikTok no tiene los permisos necesarios para publicar. Reconectala desde /conexiones para otorgarlos.',
    },
  ],
  [
    'needs_reauth',
    {
      title:   'Reconectá TikTok',
      message: 'Tu cuenta TikTok necesita autorización nueva. Andá a /conexiones y volvé a conectar.',
    },
  ],
  [
    'sqs_enqueue_failed',
    {
      message: 'Hubo un problema al enviar tu publicación al servidor. Reintentá en unos segundos.',
    },
  ],
  [
    'server_misconfig',
    {
      title:   'Configuración del servidor incompleta',
      message: 'Falta una variable de entorno en el deploy. Contactanos, lo arreglamos en seguida.',
    },
  ],
  [
    /lambda_(error|outer_error)/,
    {
      title:   'Error inesperado en el worker de publicación',
      message: 'Algo falló en nuestro servidor al procesar este post. Ya quedó registrado; reintentá y si persiste, escribinos.',
    },
  ],
  [
    /video.*(too_long|too_large|invalid|format)/i,
    {
      title:   'Video no aceptado por TikTok',
      message: 'TikTok rechazó el archivo (formato, duración o tamaño). Verificá que sea MP4 con duración válida.',
    },
  ],
  [
    /^tiktok_init_error/,
    {
      title:   'TikTok rechazó la subida',
      message: 'TikTok devolvió un error al iniciar el upload. Mirá el detalle técnico abajo o reintentá.',
    },
  ],
];

const TRUNCATE_AT = 220;

export function humanizePublishError(raw: string | null | undefined): HumanizedPublishError {
  if (!raw) return { message: '', recognized: false };

  for (const [matcher, humanized] of PATTERNS) {
    const matches = typeof matcher === 'string' ? raw.includes(matcher) : matcher.test(raw);
    if (matches) return { ...humanized, raw, recognized: true };
  }

  // Fallback: raw text, truncated, marked as unrecognized so the caller can
  // style it more conservatively.
  const truncated = raw.length > TRUNCATE_AT ? `${raw.slice(0, TRUNCATE_AT)}…` : raw;
  return { message: truncated, raw, recognized: false };
}
